import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Fade,
  Backdrop,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface ConfirmPopupProps {
  open: boolean;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onCancel: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

const ConfirmPopup: React.FC<ConfirmPopupProps> = ({
  open,
  title,
  message,
  confirmText = "Apply",
  cancelText = "Cancel",
  onCancel,
  onConfirm,
  loading = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          maxWidth: { xs: "90vw", sm: "400px" },
          width: { xs: "90vw", sm: "400px" },
          margin: { xs: "16px", sm: "32px" },
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background:
              "linear-gradient(90deg, #ff9800 0%, #f57c00 50%, #ff9800 100%)",
          },
        }}
      >
        <DialogTitle
          id="confirm-dialog-title"
          sx={{
            padding: { xs: "24px 24px 16px", sm: "32px 32px 20px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            "& .MuiDialogTitle-root": {
              padding: 0,
            },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#fff3e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": {
                  boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
                },
                "50%": {
                  boxShadow: "0 4px 20px rgba(255, 152, 0, 0.5)",
                },
                "100%": {
                  boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
                },
              },
            }}
          >
            <WarningAmberIcon
              sx={{
                fontSize: 32,
                color: "#ff9800",
                filter: "drop-shadow(0 2px 4px rgba(255, 152, 0, 0.3))",
              }}
            />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#e65100",
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              letterSpacing: "-0.02em",
            }}
          >
            {title || "Are you sure?"}
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: { xs: "0 24px 24px", sm: "0 32px 32px" },
            textAlign: "center",
            minHeight: "auto",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.95rem", sm: "1rem" },
              color: "#424242",
              lineHeight: 1.6,
              fontWeight: 400,
              maxWidth: "100%",
              wordBreak: "break-word",
            }}
          >
            {message || "This action cannot be undone."}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "0 24px 24px", sm: "0 32px 32px" },
            justifyContent: "center",
            gap: "12px",
          }}
        >
          <Button
            onClick={onCancel}
            variant="outlined"
            disabled={loading}
            sx={{
              color: "#6b7280",
              borderColor: "#d1d5db",
              backgroundColor: "#ffffff",
              padding: { xs: "10px 22px", sm: "12px 26px" },
              fontWeight: 600,
              borderRadius: "12px",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              textTransform: "none",
              minWidth: { xs: "120px", sm: "140px" },
              height: { xs: "44px", sm: "48px" },
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#ff9800",
              color: "#ffffff",
              padding: { xs: "12px 24px", sm: "14px 28px" },
              fontWeight: 600,
              borderRadius: "12px",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              textTransform: "none",
              minWidth: { xs: "120px", sm: "140px" },
              height: { xs: "44px", sm: "48px" },
              boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#f57c00",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(255, 152, 0, 0.4)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: "0 2px 8px rgba(255, 152, 0, 0.3)",
              },
            }}
          >
            {confirmText}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ConfirmPopup;


